/**
 * CharacterStatsComponent - health and stats management for actors.
 */

import { EngineClass } from '../systems/ClassRegistry.js';
import { Delegate } from '../utils/Delegate.js';
import { property } from '../utils/serialization/decorator.js';

import { NON_SPATIAL, SceneComponent } from './SceneComponent.js';

import type { SceneComponentOptions } from './SceneComponent.js';
import type * as THREE from 'three';

export interface CharacterStatsComponentOptions extends SceneComponentOptions {
  maxHealth?: number;
  healthRegen?: number;
  attackCooldown?: number;
  attackRange?: number;
  attackDamage?: number;
  speed?: number;
}

export interface DamageHitInfo {
  hitLocation: THREE.Vector3;
  hitNormal: THREE.Vector3;
}

@EngineClass('CharacterStatsComponent')
export class CharacterStatsComponent extends SceneComponent {

  static readonly [NON_SPATIAL] = true;


  // Core stats
  @property({ type: 'number', min: 1, step: 1, decimals: 0, description: 'Maximum health points' })
  protected maxHealth: number = 100;

  private currentHealth: number = 100;

  @property({ type: 'number', min: 0, step: 0.1, decimals: 1, description: 'Health regeneration per second' })
  protected healthRegen: number = 1;

  private isDead: boolean = false;

  // Combat stats
  @property({ type: 'number', min: 0, step: 0.1, decimals: 1, description: 'Cooldown between attacks in seconds' })
  protected attackCooldown: number = 1.0;

  @property({ type: 'number', min: 0, step: 0.1, decimals: 1, description: 'Attack range in units' })
  protected attackRange: number = 2.0;

  @property({ type: 'number', min: 0, step: 1, decimals: 0, description: 'Damage dealt per attack' })
  protected attackDamage: number = 10;

  // Movement stats
  @property({ type: 'number', min: 0, step: 0.1, decimals: 1, description: 'Movement speed in units per second' })
  protected speed: number = 5.0;

  // Events
  public readonly onHealthChanged = new Delegate<[number, number]>();
  public readonly onDeath = new Delegate<[DamageHitInfo | undefined]>();

  // Custom death handler
  protected handleDeath(hitInfo?: DamageHitInfo): void {
    const actor = this.getActor();
    if (actor) {
      actor.handleDeath(hitInfo);
    }
  }

  public override initialize(options?: CharacterStatsComponentOptions): void {
    super.initialize(options);
    this.maxHealth = options?.maxHealth ?? this.maxHealth;
    this.healthRegen = options?.healthRegen ?? this.healthRegen;
    this.attackCooldown = options?.attackCooldown ?? this.attackCooldown;
    this.attackRange = options?.attackRange ?? this.attackRange;
    this.attackDamage = options?.attackDamage ?? this.attackDamage;
    this.speed = options?.speed ?? this.speed;
    this.currentHealth = this.maxHealth;
  }

  public override postLoad(): void {
    super.postLoad();
    this.currentHealth = this.maxHealth;
  }

  public override beginPlay(): void {
    super.beginPlay();
    this.currentHealth = this.maxHealth;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.tick(deltaTime);
  }

  private tick(deltaTime: number): void {
    if (this.currentHealth < this.maxHealth) {
      this.heal(this.healthRegen * deltaTime);
    }
  }

  /**
   * Applies damage to the character.
   * @param amount - Amount of damage to apply
   * @param hitInfo - Optional hit information
   * @returns The actual amount of damage dealt
   */
  public takeDamage(amount: number, hitInfo?: DamageHitInfo): number {
    if (this.isDead) {
      return 0;
    }

    const previousHealth = this.currentHealth;
    this.currentHealth = Math.max(0, this.currentHealth - amount);

    this.onHealthChanged.invoke(this.currentHealth, this.maxHealth);

    if (this.currentHealth <= 0) {
      this.isDead = true;
      this.onDeath.invoke(hitInfo);
      this.handleDeath(hitInfo);
    }

    return previousHealth - this.currentHealth;
  }

  /**
   * Heals the character.
   * @param amount - Amount of healing to apply
   * @returns The actual amount healed
   */
  public heal(amount: number): number {
    if (this.isDead) {
      return 0;
    }

    if (!amount) {
      return 0;
    }

    const previousHealth = this.currentHealth;
    this.currentHealth = Math.min(this.maxHealth, this.currentHealth + amount);

    this.onHealthChanged.invoke(this.currentHealth, this.maxHealth);

    return this.currentHealth - previousHealth;
  }

  public getIsDead(): boolean { return this.isDead; }
  public getMaxHealth(): number { return this.maxHealth; }
  public getCurrentHealth(): number { return this.currentHealth; }
  public getHealthRegen(): number { return this.healthRegen; }
  public getAttackCooldown(): number { return this.attackCooldown; }
  public getAttackRange(): number { return this.attackRange; }
  public getSpeed(): number { return this.speed; }
  public getAttackDamage(): number { return this.attackDamage; }

  public setMaxHealth(value: number): void {
    this.maxHealth = value;
    this.currentHealth = Math.min(this.currentHealth, this.maxHealth);
    this.onHealthChanged.invoke(this.currentHealth, this.maxHealth);
  }

  public setHealthRegen(value: number): void { this.healthRegen = value; }
  public setAttackCooldown(value: number): void { this.attackCooldown = value; }
  public setAttackRange(value: number): void { this.attackRange = value; }
  public setSpeed(value: number): void { this.speed = value; }
  public setAttackDamage(value: number): void { this.attackDamage = value; }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_CharacterStats';
  }
}
